import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { ShopState } from '../_state/shop/shop.state';
import { environment } from '../../environments/environment';
import { BannerStateService } from './banner-state.service';
import { ImportProgressService } from './import-progress.service';
import { ElixirImportProgressService } from './elixir-import-progress.service';
import { LocalStorageService } from '../core/services/local-storage.service';
import { LocalStorageKeys } from '../core/constants/local-storage-keys.enum';

export interface ImportStartRequest {
  shopId: string;
  startDate?: string;
  endDate?: string;
  merchantId?: string; // Optional, will be auto-filled if not provided
  stages?: string[]; // Optional, run only specific import stages
  silent?: boolean; // Optional, don't show import banner on UI
}

export interface ImportStartResponse {
  success: boolean;
  message?: string;
  importId?: string;
  sagaId?: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ShopImportService {
  private readonly elixirApiUrl = (environment as any).elixirApiUrl || 'http://localhost:4001';
  private readonly nodeApiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private store: Store,
    private bannerState: BannerStateService,
    private nodeImportService: ImportProgressService,
    private elixirImportService: ElixirImportProgressService,
    private localStorage: LocalStorageService
  ) {}

  /**
   * Start import for a shop
   * Automatically selects backend based on feature flag
   */
  startImport(request: ImportStartRequest): Observable<ImportStartResponse> {
    // Get merchant ID from store if not provided
    if (!request.merchantId) {
      const activeShop = this.store.selectSnapshot(ShopState.activeShop);
      if (!activeShop) {
        return throwError(() => new Error('No active shop found. Please select a shop first.'));
      }
      request.merchantId = activeShop.merchantId;

      // If shopId not provided, use active shop's ID
      if (!request.shopId) {
        request.shopId = activeShop.shopId || activeShop.id;
      }
    }

    // Set default dates if not provided
    if (!request.startDate) {
      request.startDate = '2020-01-01';
    }
    if (!request.endDate) {
      const today = new Date();
      request.endDate = today.toISOString().split('T')[0];
    }

    // CRITICAL FIX: Always use Node.js backend endpoint
    // Node.js backend will proxy to Elixir if feature flag is enabled
    // This avoids CORS issues and ensures consistent behavior
    return this.startNodeImport(request);
  }

  /**
   * Start import using Elixir backend
   */
  private startElixirImport(request: ImportStartRequest): Observable<ImportStartResponse> {
    const url = `${this.elixirApiUrl}/api/imports/start`;
    const headers = this.getHeaders();

    const body: Record<string, any> = {
      merchant_id: request.merchantId,
      shop_id: request.shopId,
      start_date: request.startDate,
      end_date: request.endDate,
      use_real_credentials: true
    };

    // Add optional stages parameter for selective imports
    if (request.stages && request.stages.length > 0) {
      body['stages'] = request.stages;
    }

    // Add optional silent parameter to suppress banner
    if (request.silent !== undefined) {
      body['silent'] = request.silent;
    }

    return this.http.post<ImportStartResponse>(url, body, { headers }).pipe(
      tap(response => {
        // Set import in progress flag
        this.bannerState.setDataImportInProgress(true);

        // Subscribe to WebSocket for this shop
        if (request.shopId) {
          this.elixirImportService.subscribeToShop(request.shopId);
        }

        // Store import info in localStorage for recovery after page refresh
        this.localStorage.set(
          LocalStorageKeys.ACTIVE_IMPORT,
          JSON.stringify({
            shopId: request.shopId,
            merchantId: request.merchantId,
            startedAt: Date.now(),
            backend: 'elixir'
          })
        );
      }),
      catchError(error => {
        this.bannerState.setDataImportInProgress(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Start import using Node.js backend
   */
  private startNodeImport(request: ImportStartRequest): Observable<ImportStartResponse> {
    const url = `${this.nodeApiUrl}/auth/import-with-flow`;
    const headers = this.getHeaders();

    const body: Record<string, any> = {
      shopId: request.shopId,
      startDate: request.startDate,
      endDate: request.endDate
    };

    // Add optional stages parameter for selective imports
    if (request.stages && request.stages.length > 0) {
      body['stages'] = request.stages;
    }

    // Add optional silent parameter to suppress banner
    if (request.silent !== undefined) {
      body['silent'] = request.silent;
    }

    return this.http.post<any>(url, body, { headers }).pipe(
      map(
        response =>
          ({
            success: true,
            message: 'Import started',
            data: response
          }) as ImportStartResponse
      ),
      tap(response => {
        // CRITICAL FIX: Determine which backend actually handled the import
        // Node.js backend may proxy to Elixir if feature flag is enabled
        const actualBackend = response.data?.backend || 'nodejs';

        // Set import in progress flag IMMEDIATELY
        this.bannerState.setDataImportInProgress(true);

        // Subscribe to correct WebSocket based on actual backend
        if (request.shopId) {
          if (actualBackend === 'elixir') {
            // Import went through Elixir - subscribe to Elixir WebSocket
            this.elixirImportService.subscribeToShop(request.shopId);

            // CRITICAL FIX: Force check for active imports to update banner immediately
            // This ensures banner shows correct data even if WebSocket events are missed
            setTimeout(() => {
              (this.elixirImportService as any).checkActiveImportsFromBackend?.();
            }, 500);
          } else {
            // Import went through Node.js - subscribe to Node.js WebSocket
            this.nodeImportService.subscribeToShop(request.shopId);
          }
        }

        // Store import info in localStorage for recovery after page refresh
        this.localStorage.set(
          LocalStorageKeys.ACTIVE_IMPORT,
          JSON.stringify({
            shopId: request.shopId,
            merchantId: request.merchantId,
            startedAt: Date.now(),
            backend: actualBackend
          })
        );
      }),
      catchError(error => {
        this.bannerState.setDataImportInProgress(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Helper method for Postman testing
   * Can be called from browser console:
   * window.testImport = (shopId, startDate, endDate) => { ... }
   */
  testImportFromConsole(shopId?: string, startDate?: string, endDate?: string): void {
    this.startImport({
      shopId: shopId || '7495918220169153453',
      startDate: startDate || '2020-01-01',
      endDate: endDate || new Date().toISOString().split('T')[0]
    }).subscribe({
      next: response => {
        alert(`Import started successfully! Check the import banner at the top of the page.`);
      },
      error: error => {
        alert(`Import failed: ${error.message || 'Unknown error'}`);
      }
    });
  }

  /**
   * Check import status (for debugging)
   */
  checkImportStatus(shopId: string): Observable<any> {
    const url = `${this.elixirApiUrl}/api/imports/progress/${shopId}`;
    return this.http.get(url);
  }

  /**
   * Cancel import
   */
  cancelImport(importId: string): Observable<any> {
    const url = `${this.elixirApiUrl}/api/imports/${importId}/cancel`;
    return this.http.post(url, {});
  }

  /**
   * Get headers with auth token
   */
  private getHeaders(): HttpHeaders {
    const token = this.localStorage.get<string>(LocalStorageKeys.ACCESS_TOKEN);
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : ''
    });
  }

  /**
   * Expose service to window for Postman/console testing
   */
  exposeToWindow(): void {
    (window as any).shopImportService = this;
    (window as any).testImport = (shopId?: string, startDate?: string, endDate?: string) => {
      this.testImportFromConsole(shopId, startDate, endDate);
    };
  }
}
